import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, Type, Square, Image as ImageIcon, Layers as LayersIcon, Trash2,
  ArrowUp, ArrowDown, Save, Loader2, ArrowLeft, Eye, EyeOff, Copy, Search, User,
  AlignLeft, AlignCenter, AlignRight, ArrowUpToLine, ArrowDownToLine, Minus,
  Undo2, Redo2, Film, Play, Sparkles, Lock, Unlock, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import GovernancePanel from '@/components/collateral/GovernancePanel';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';
import { collateralApi, DYNAMIC_FIELDS, buildRepeatGroup } from '@/api/collateral';
import CollateralImagePicker from '@/components/CollateralImagePicker';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const PRESETS = [
  { key: 'social_square', label: 'Social square (1080×1080)', width: 1080, height: 1080, dpi: 72, category: 'social' },
  { key: 'social_story', label: 'Social story (1080×1920)', width: 1080, height: 1920, dpi: 72, category: 'social' },
  { key: 'business_card', label: 'Business card (85×55mm)', width: 1004, height: 650, dpi: 300, category: 'print' },
  { key: 'a5_flyer', label: 'A5 flyer (148×210mm)', width: 1748, height: 2480, dpi: 300, category: 'print' },
];

// Per-layer motion presets (mirror backend AnimationPreset). `timed` presets
// take a delay + duration; ken_burns runs continuously over the whole clip.
const ANIM_PRESETS = [
  { key: 'none', label: 'None', timed: false },
  { key: 'fade', label: 'Fade in', timed: true },
  { key: 'slide_up', label: 'Slide up', timed: true },
  { key: 'slide_down', label: 'Slide down', timed: true },
  { key: 'slide_left', label: 'Slide from left', timed: true },
  { key: 'slide_right', label: 'Slide from right', timed: true },
  { key: 'pop', label: 'Pop in', timed: true },
  { key: 'ken_burns', label: 'Ken Burns zoom', timed: false },
];

// Font families the renderer can resolve (see backend collateral/fonts.py).
const FONT_FAMILIES = [
  { key: 'sans', label: 'Sans' },
  { key: 'heading', label: 'Heading' },
  { key: 'serif', label: 'Serif' },
  { key: 'mono', label: 'Mono' },
];
// Approximate CSS stacks for the canvas preview only (true output uses the
// server fonts above).
const FONT_CSS = {
  sans: 'system-ui, Arial, sans-serif',
  heading: '"Montserrat", system-ui, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: '"Courier New", ui-monospace, monospace',
};

// Model gradient angle is 0 = L→R, 90 = T→B; CSS 0deg = bottom→top, so +90.
const cssGradient = (g) => `linear-gradient(${(g?.angle ?? 90) + 90}deg, ${g?.from_color || '#154734'}, ${g?.to_color || '#0e3527'})`;

const SNAP = 8; // artboard-px threshold for edge/centre snapping

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

const blankLayer = (type) => {
  const base = { id: uid('lyr'), type, x: 60, y: 60, width: 300, height: 120, visible: true, locked: false, opacity: 1 };
  if (type === 'text') return { ...base, text: 'New text', font_family: 'sans', font_size: 40, font_weight: 'regular', color: '#154734', align: 'left', line_height: 1.2, uppercase: false, stroke_width: 0, stroke_color: null, shadow_color: null, shadow_offset: 4 };
  if (type === 'shape') return { ...base, shape: 'rect', fill_color: '#154734', stroke_color: null, stroke_width: 0, border_radius: 0 };
  if (type === 'image') return { ...base, source: '', fit: 'cover', border_radius: 0 };
  if (type === 'background') return { ...base, x: 0, y: 0, color: '#ffffff', image: null, fit: 'cover' };
  return base;
};

const newTemplate = () => ({
  name: 'Untitled template',
  category: 'social',
  formats: ['png'],
  artboard: { width: 1080, height: 1080, dpi: 72, background_color: '#ffffff' },
  layers: [],
  // Authored templates start as Draft and go live via the publish gate, so
  // nothing reaches all agents unreviewed (mirrors Alchemy MediaItem governance).
  visibility: {
    status: 'draft', effective_date: null, expiry_date: null,
    restricted_regions: [], restricted_offices: [], restricted_employees: [],
    branch_only: false, franchise_only: false,
  },
});

// Pam Golding brand palette — one-click swatches in every colour field.
const BRAND_SWATCHES = ['#154734', '#cfe0d8', '#ba0c2f', '#1f2937', '#ffffff', '#000000'];

// Module-scope so it isn't remounted each render (which would drop input focus).
function ColorField({ label, value, onChange, recent = [], prefix = '' }) {
  const chips = [...BRAND_SWATCHES, ...recent.filter((c) => !BRAND_SWATCHES.includes(c))].slice(0, 12);
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <div className="flex gap-1">
        <Input type="color" className="h-8 w-10 p-1" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} />
        <Input className="h-8 flex-1 text-xs font-mono" value={value || ''} onChange={(e) => onChange(e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {chips.map((c) => (
          <button
            key={c}
            type="button"
            title={c}
            onClick={() => onChange(c)}
            data-testid={`swatch-${prefix ? `${prefix}-` : ''}${c.replace('#', '')}`}
            style={{ background: c }}
            className={`h-5 w-5 rounded border ${value?.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-primary ring-offset-1' : 'border-border'}`}
          />
        ))}
      </div>
    </div>
  );
}

// Gradient fill editor (shapes + backgrounds). `value` is {from_color,to_color,angle}
// or null; onChange(null) turns it off.
function GradientField({ value, recent = [], onChange }) {
  const on = !!value;
  return (
    <div className="space-y-2">
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-xs font-medium">Gradient fill</span>
        <input type="checkbox" checked={on} data-testid="gradient-toggle"
          onChange={(e) => onChange(e.target.checked ? { from_color: '#154734', to_color: '#0e3527', angle: 90 } : null)} />
      </label>
      {on && (
        <>
          <div className="h-6 rounded border" style={{ background: cssGradient(value) }} />
          <ColorField label="From" prefix="grad-from" value={value.from_color} recent={recent} onChange={(v) => onChange({ ...value, from_color: v })} />
          <ColorField label="To" prefix="grad-to" value={value.to_color} recent={recent} onChange={(v) => onChange({ ...value, to_color: v })} />
          <div>
            <Label className="text-[11px]">Angle — {Math.round(value.angle ?? 90)}°</Label>
            <input type="range" min="0" max="360" step="5" value={value.angle ?? 90} data-testid="gradient-angle"
              onChange={(e) => onChange({ ...value, angle: Number(e.target.value) })} className="w-full accent-[#154734]" />
          </div>
        </>
      )}
    </div>
  );
}

export default function CollateralEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [template, setTemplate] = useState(newTemplate());
  const [selectedIds, setSelectedIds] = useState([]);
  const selectOnly = useCallback((id) => setSelectedIds(id ? [id] : []), []);
  const toggleSelect = useCallback(
    (id) => setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])),
    [],
  );
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [guides, setGuides] = useState({ v: null, h: null });

  // Preview
  const [agentQuery, setAgentQuery] = useState('');
  const debouncedAgentQuery = useDebouncedValue(agentQuery, 300);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const previewUrlRef = useRef(null);

  // MP4 preview (only relevant when the template is animated).
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoPreviewing, setVideoPreviewing] = useState(false);
  const videoUrlRef = useRef(null);

  const animated = !!template.video;
  // Single-layer property editing applies only when exactly one layer is selected.
  const selected = selectedIds.length === 1 ? (template.layers.find((l) => l.id === selectedIds[0]) || null) : null;

  // Colours already used in this template — offered as swatches alongside brand.
  const recentColors = Array.from(new Set(
    template.layers
      .flatMap((l) => [l.color, l.fill_color, l.stroke_color])
      .concat(template.artboard.background_color)
      .filter((c) => typeof c === 'string' && c.startsWith('#'))
  ));

  // Live mirror of state for event handlers (drag / keyboard) — avoids stale closures.
  const stateRef = useRef({});
  stateRef.current.template = template;
  stateRef.current.selectedIds = selectedIds;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const [editingId, setEditingId] = useState(null);

  // Image-library picker (which layer it targets).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [govOpen, setGovOpen] = useState(false);
  const [rptOpen, setRptOpen] = useState(false);
  const [rptCfg, setRptCfg] = useState({ bind: 'agents', layout: 'columns', gap: 12, minCount: 1, maxCount: 3 });
  const pickerLayerRef = useRef(null);

  // Uploaded brand fonts (custom:<id>) offered in the font picker.
  const [customFonts, setCustomFonts] = useState([]);
  const [loadedFonts, setLoadedFonts] = useState({}); // id -> css family, once FontFace-loaded
  const fontUploadRef = useRef(null);

  // Map a layer's font_family to a CSS stack for the canvas preview — using
  // the real uploaded face once it has loaded, else the brand fallback.
  const cssFontFor = (fam) => {
    if (fam && fam.startsWith('custom:')) return loadedFonts[fam.slice(7)] || FONT_CSS.sans;
    return FONT_CSS[fam] || FONT_CSS.sans;
  };

  // Undo/redo history — refs for the stacks, a tick to refresh button state.
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [, bumpHist] = useState(0);
  const refreshHist = useCallback(() => bumpHist((n) => n + 1), []);

  const snapshot = useCallback(() => {
    pastRef.current = [...pastRef.current, stateRef.current.template].slice(-60);
    futureRef.current = [];
    refreshHist();
  }, [refreshHist]);

  // Discrete change: records a history entry. `mutateLive` skips history —
  // used for continuous drag/resize/typing that snapshots once at the start.
  const mutate = useCallback((updater) => { snapshot(); setTemplate(updater); setDirty(true); }, [snapshot]);
  const mutateLive = useCallback((updater) => { setTemplate(updater); setDirty(true); }, []);

  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    const prev = pastRef.current[pastRef.current.length - 1];
    futureRef.current = [stateRef.current.template, ...futureRef.current];
    pastRef.current = pastRef.current.slice(0, -1);
    setEditingId(null);
    setTemplate(prev); setDirty(true); refreshHist();
  }, [refreshHist]);

  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    const next = futureRef.current[0];
    pastRef.current = [...pastRef.current, stateRef.current.template];
    futureRef.current = futureRef.current.slice(1);
    setEditingId(null);
    setTemplate(next); setDirty(true); refreshHist();
  }, [refreshHist]);

  // ---- load (edit mode) ----
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await collateralApi.getTemplate(id);
        if (cancelled) return;
        if (data?.data?.is_system) {
          toast.error('System templates are read-only. Duplicate it to edit.');
          navigate('/collateral/templates');
          return;
        }
        setTemplate(data.data);
        setDirty(false);
        pastRef.current = [];
        futureRef.current = [];
      } catch (e) {
        toast.error('Failed to load template');
        navigate('/collateral/templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isEdit, navigate]);

  // ---- agent search for preview ----
  useEffect(() => {
    let cancelled = false;
    const q = debouncedAgentQuery.trim();
    if (q.length < 2) { setAgents([]); return; }
    (async () => {
      try {
        const res = await apiV1.get('/reference-data/agents/list', { params: { search: q, per_page: 20 } });
        if (!cancelled) setAgents(res.data?.data || []);
      } catch { if (!cancelled) setAgents([]); }
    })();
    return () => { cancelled = true; };
  }, [debouncedAgentQuery]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
  }, []);

  // ---- custom brand fonts ----
  useEffect(() => { collateralApi.listFonts().then(setCustomFonts).catch(() => {}); }, []);

  // Load each uploaded font into the browser so the canvas shows the real face.
  useEffect(() => {
    if (typeof FontFace === 'undefined' || !customFonts.length) return undefined;
    let cancelled = false;
    (async () => {
      for (const f of customFonts) {
        const id = String(f.family).replace('custom:', '');
        if (loadedFonts[id]) continue;
        try {
          const blob = await collateralApi.fontFile(id);
          const face = new FontFace(`cf_${id}`, `url(${URL.createObjectURL(blob)})`);
          await face.load();
          document.fonts.add(face);
          if (!cancelled) setLoadedFonts((prev) => ({ ...prev, [id]: `cf_${id}` }));
        } catch { /* fall back to brand sans */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFonts]);

  const handleFontUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const f = await collateralApi.uploadFont(file);
      setCustomFonts(await collateralApi.listFonts());
      if (f?.family && selected) updateLayer(selected.id, { font_family: f.family });
      toast.success('Font uploaded');
    } catch (err) {
      toast.error('Font upload failed');
    }
  };

  // ---- warn on tab-close with unsaved changes ----
  useEffect(() => {
    const onBeforeUnload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // ---- layer ops ----
  const patchTemplate = (patch) => mutate((t) => ({ ...t, ...patch }));
  const patchArtboard = (patch) => mutate((t) => ({ ...t, artboard: { ...t.artboard, ...patch } }));

  // Turn a template into an animated (MP4) one, or back to a still. Animation
  // implies MP4 export; a still template exports PNG.
  const toggleAnimation = (on) => {
    patchTemplate(on
      ? { video: { duration: 5, fps: 24 }, formats: ['mp4', 'gif'] }
      : { video: null, formats: ['png'] });
  };
  const patchVideo = (patch) => mutate((t) => ({ ...t, video: { ...(t.video || { duration: 5, fps: 24 }), ...patch } }));

  const setLayerAnim = (layerId, patch) => {
    mutate((t) => ({
      ...t,
      layers: t.layers.map((l) => {
        if (l.id !== layerId) return l;
        const next = { ...(l.animation || { preset: 'none', delay: 0, duration: 0.6 }), ...patch };
        return { ...l, animation: next.preset === 'none' ? null : next };
      }),
    }));
  };

  const updateLayer = useCallback((layerId, patch, opts) => {
    const apply = opts && opts.live ? mutateLive : mutate;
    apply((t) => ({ ...t, layers: t.layers.map((l) => (l.id === layerId ? { ...l, ...patch } : l)) }));
  }, [mutate, mutateLive]);

  const nudgeLayers = useCallback((ids, dx, dy) => {
    const set = new Set(ids);
    mutate((t) => ({
      ...t,
      layers: t.layers.map((l) => (set.has(l.id) && !l.locked ? { ...l, x: Math.round((l.x || 0) + dx), y: Math.round((l.y || 0) + dy) } : l)),
    }));
  }, [mutate]);

  const toggleLock = (layerId) => {
    mutate((t) => ({ ...t, layers: t.layers.map((l) => (l.id === layerId ? { ...l, locked: !l.locked } : l)) }));
  };

  const addLayer = (type) => {
    const layer = blankLayer(type);
    if (type !== 'background') {
      const A = stateRef.current.template.artboard;
      const cascade = (stateRef.current.template.layers.length % 5) * 24;
      layer.x = Math.round((A.width - layer.width) / 2) + cascade;
      layer.y = Math.round((A.height - layer.height) / 2) + cascade;
    }
    mutate((t) => ({ ...t, layers: [...t.layers, layer] }));
    selectOnly(layer.id);
  };

  const deleteLayer = useCallback((layerId) => {
    mutate((t) => ({ ...t, layers: t.layers.filter((l) => l.id !== layerId) }));
    setSelectedIds((cur) => cur.filter((x) => x !== layerId));
  }, [mutate]);

  const deleteLayers = useCallback((ids) => {
    const set = new Set(ids);
    mutate((t) => ({ ...t, layers: t.layers.filter((l) => !set.has(l.id)) }));
    setSelectedIds([]);
  }, [mutate]);

  const duplicateLayer = useCallback((layerId) => {
    const src = stateRef.current.template.layers.find((l) => l.id === layerId);
    if (!src) return;
    const clone = { ...src, id: uid('lyr'), x: (src.x || 0) + 20, y: (src.y || 0) + 20 };
    mutate((t) => {
      const i = t.layers.findIndex((l) => l.id === layerId);
      const layers = [...t.layers];
      layers.splice(i + 1, 0, clone);
      return { ...t, layers };
    });
    selectOnly(clone.id);
  }, [mutate, selectOnly]);

  const duplicateLayers = useCallback((ids) => {
    const set = new Set(ids);
    const cloneIds = [];
    mutate((t) => {
      const layers = [];
      for (const l of t.layers) {
        layers.push(l);
        if (set.has(l.id)) {
          const clone = { ...l, id: uid('lyr'), x: (l.x || 0) + 20, y: (l.y || 0) + 20 };
          cloneIds.push(clone.id);
          layers.push(clone);
        }
      }
      return { ...t, layers };
    });
    setSelectedIds(cloneIds);
  }, [mutate]);

  const toggleVisibility = (layerId) => {
    mutate((t) => ({ ...t, layers: t.layers.map((l) => (l.id === layerId ? { ...l, visible: l.visible === false } : l)) }));
  };

  const moveLayer = (layerId, dir) => {
    // The list is rendered reversed (front-most at top), and higher array
    // index = drawn later = in front. So "up" in the list means moving toward
    // the END of the array (forward), and "down" toward the start (backward).
    mutate((t) => {
      const i = t.layers.findIndex((l) => l.id === layerId);
      const j = dir === 'up' ? i + 1 : i - 1;
      if (i < 0 || j < 0 || j >= t.layers.length) return t;
      const layers = [...t.layers];
      [layers[i], layers[j]] = [layers[j], layers[i]];
      return { ...t, layers };
    });
  };

  const alignLayer = (axis, pos) => {
    if (!selectedIds.length) return;
    const A = template.artboard;
    const set = new Set(selectedIds);
    mutate((t) => ({
      ...t,
      layers: t.layers.map((l) => {
        if (!set.has(l.id)) return l;
        if (axis === 'h') {
          const x = pos === 'left' ? 0 : pos === 'right' ? A.width - l.width : Math.round((A.width - l.width) / 2);
          return { ...l, x };
        }
        const y = pos === 'top' ? 0 : pos === 'bottom' ? A.height - l.height : Math.round((A.height - l.height) / 2);
        return { ...l, y };
      }),
    }));
  };

  const applyPreset = (key) => {
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    patchTemplate({ category: p.category });
    patchArtboard({ width: p.width, height: p.height, dpi: p.dpi });
  };

  // ---- keyboard shortcuts ----
  const handleSaveRef = useRef(() => {});
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
      const k = e.key;
      if ((e.metaKey || e.ctrlKey) && k.toLowerCase() === 's') { e.preventDefault(); handleSaveRef.current(); return; }
      if (typing) return;
      if ((e.metaKey || e.ctrlKey) && k.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if ((e.metaKey || e.ctrlKey) && k.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
      const sel = stateRef.current.selectedIds || [];
      if ((e.metaKey || e.ctrlKey) && k.toLowerCase() === 'd') { e.preventDefault(); if (sel.length) duplicateLayers(sel); return; }
      if (k === 'Escape') { setSelectedIds([]); return; }
      if (!sel.length) return;
      if (k === 'Delete' || k === 'Backspace') { e.preventDefault(); deleteLayers(sel); return; }
      if (k.startsWith('Arrow')) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const d = { ArrowUp: [0, -step], ArrowDown: [0, step], ArrowLeft: [-step, 0], ArrowRight: [step, 0] }[k];
        if (d) nudgeLayers(sel, d[0], d[1]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteLayers, duplicateLayers, nudgeLayers, undo, redo]);

  // ---- canvas drag + snap ----
  // The canvas fits the (measured) column width and a max height while keeping
  // the artboard's aspect ratio — a fixed-width box would get squished by the
  // flex container on narrow screens.
  const canvasWrapRef = useRef(null);
  const [canvasW, setCanvasW] = useState(460);
  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setCanvasW(Math.max(160, el.clientWidth - 32)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const CANVAS_MAX_H = 540;
  const [zoom, setZoom] = useState(1); // 1 = fit; user zoom multiplies the fit scale
  const fitScale = Math.min(canvasW / template.artboard.width, CANVAS_MAX_H / template.artboard.height, 1.5);
  const scale = fitScale * zoom;

  const startDrag = (e, layer) => {
    e.stopPropagation();
    const sel = stateRef.current.selectedIds || [];
    // Shift/Cmd/Ctrl-click toggles selection without starting a drag.
    if (e.shiftKey || e.metaKey || e.ctrlKey) { toggleSelect(layer.id); return; }
    // Locked layers select but don't drag (so the user can unlock them).
    if (layer.locked) { selectOnly(layer.id); return; }
    // Drag the whole selection if this layer is part of a multi-selection;
    // otherwise select just it and drag it. Locked members stay put.
    const groupIds = (sel.includes(layer.id) && sel.length > 1 ? sel : [layer.id])
      .filter((gid) => { const gl = stateRef.current.template.layers.find((x) => x.id === gid); return gl && !gl.locked; });
    if (!sel.includes(layer.id)) selectOnly(layer.id);
    snapshot(); // one history entry per drag gesture
    const startX = e.clientX, startY = e.clientY;
    const ox = layer.x, oy = layer.y, w = layer.width, h = layer.height;
    const starts = {};
    for (const gl of stateRef.current.template.layers) {
      if (groupIds.includes(gl.id)) starts[gl.id] = { x: gl.x, y: gl.y };
    }
    const onMove = (ev) => {
      const t = stateRef.current.template;
      const A = t.artboard;
      let x = Math.round(ox + (ev.clientX - startX) / scale);
      let y = Math.round(oy + (ev.clientY - startY) / scale);

      // Snap the primary layer's nearest box handle (left/centre/right ·
      // top/middle/bottom) to the nearest target line — artboard edges/centre
      // AND sibling layers' edges/centres — picking the closest within range.
      const others = t.layers.filter((l) => !groupIds.includes(l.id) && l.visible !== false && l.type !== 'background');
      const targetsX = [0, A.width / 2, A.width];
      const targetsY = [0, A.height / 2, A.height];
      for (const o of others) {
        targetsX.push(o.x, o.x + o.width / 2, o.x + o.width);
        targetsY.push(o.y, o.y + o.height / 2, o.y + o.height);
      }
      const snap = (pos, size, targets) => {
        const handles = [pos, pos + size / 2, pos + size];
        let best = null;
        for (const tgt of targets) for (const hd of handles) {
          const d = Math.abs(hd - tgt);
          if (d <= SNAP && (!best || d < best.d)) best = { d, adj: tgt - hd, at: tgt };
        }
        return best;
      };
      const bx = snap(x, w, targetsX);
      const by = snap(y, h, targetsY);
      let gv = null, gh = null;
      if (bx) { x = Math.round(x + bx.adj); gv = bx.at; }
      if (by) { y = Math.round(y + by.adj); gh = by.at; }
      setGuides({ v: gv, h: gh });
      // Apply the (snapped) delta to every layer in the drag group.
      const dx = x - ox, dy = y - oy;
      mutateLive((tt) => ({
        ...tt,
        layers: tt.layers.map((l) => (groupIds.includes(l.id)
          ? { ...l, x: Math.round(starts[l.id].x + dx), y: Math.round(starts[l.id].y + dy) }
          : l)),
      }));
    };
    const onUp = () => {
      setGuides({ v: null, h: null });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startResize = (e, layer) => {
    e.stopPropagation();
    snapshot(); // one history entry per resize gesture
    const startX = e.clientX, startY = e.clientY;
    const ow = layer.width, oh = layer.height;
    const onMove = (ev) => {
      updateLayer(layer.id, {
        width: Math.max(10, Math.round(ow + (ev.clientX - startX) / scale)),
        height: Math.max(10, Math.round(oh + (ev.clientY - startY) / scale)),
      }, { live: true });
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ---- save ----
  const applyRepeat = () => {
    const sel = template.layers.filter((l) => selectedIds.includes(l.id));
    if (sel.length === 0) { toast.error('Select the layer(s) to repeat first'); return; }
    const rg = buildRepeatGroup(sel, rptCfg);
    mutate((t) => ({
      ...t,
      layers: t.layers.filter((l) => !selectedIds.includes(l.id)),
      repeat_groups: [...(t.repeat_groups || []), rg],
    }));
    setSelectedIds([]);
    toast.success(`Repeating group created (${rg.bind}, ${rg.min_count}–${rg.max_count})`);
  };

  const removeRepeat = (id) =>
    mutate((t) => ({ ...t, repeat_groups: (t.repeat_groups || []).filter((g) => g.id !== id) }));

  const handleSave = async () => {
    if (!template.name.trim()) { toast.error('Give the template a name'); return; }
    setSaving(true);
    try {
      const payload = { ...template };
      if (isEdit) {
        await collateralApi.updateTemplate(id, payload);
        setDirty(false);
        toast.success('Template saved');
      } else {
        const res = await collateralApi.createTemplate(payload);
        setDirty(false);
        toast.success('Template created');
        navigate(`/collateral/templates/${res.data.id}/edit`, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  handleSaveRef.current = handleSave;

  const goBack = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    navigate('/collateral/templates');
  };

  // ---- server preview ----
  const runPreview = async () => {
    setPreviewing(true);
    try {
      const blob = await collateralApi.renderPreview({ template, agentId: selectedAgent?.id || null });
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const runVideoPreview = async () => {
    setVideoPreviewing(true);
    try {
      const blob = await collateralApi.renderPreview({ template, agentId: selectedAgent?.id || null, format: 'mp4' });
      if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
      const url = URL.createObjectURL(blob);
      videoUrlRef.current = url;
      setVideoUrl(url);
    } catch (e) {
      toast.error('Clip preview failed');
    } finally {
      setVideoPreviewing(false);
    }
  };

  const insertField = (token, isImage) => {
    if (!selected) return;
    if (selected.type === 'image') updateLayer(selected.id, { source: `{{${token}}}` });
    else if (selected.type === 'text' && !isImage) updateLayer(selected.id, { text: `${selected.text || ''}{{${token}}}` });
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const alignBtn = (axis, pos, Icon, title, testid) => (
    <button type="button" title={title} onClick={() => alignLayer(axis, pos)} data-testid={testid}
      className="p-1.5 rounded border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary">
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="space-y-4" data-testid="collateral-editor">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Templates
          </Button>
          <Input
            className="w-64 font-medium"
            value={template.name}
            onChange={(e) => patchTemplate({ name: e.target.value })}
            data-testid="template-name"
            placeholder="Template name"
          />
          <Select value="" onValueChange={applyPreset}>
            <SelectTrigger className="w-56" data-testid="preset-select"><SelectValue placeholder={`${template.artboard.width}×${template.artboard.height} · ${template.artboard.dpi}dpi`} /></SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={undo} disabled={pastRef.current.length === 0} title="Undo (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={redo} disabled={futureRef.current.length === 0} title="Redo (Ctrl+Shift+Z)" data-testid="redo-btn"><Redo2 className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setRptOpen(true)} data-testid="repeat-btn" title="Repeating groups (e.g. 1 vs 2–3 agents)">
            <LayersIcon className="h-4 w-4 mr-2" />Repeat{(template.repeat_groups?.length || 0) > 0 ? ` (${template.repeat_groups.length})` : ''}
          </Button>
          <Button variant="outline" onClick={() => setGovOpen(true)} data-testid="governance-btn" title="Publishing & visibility" className="capitalize">
            <ShieldCheck className="h-4 w-4 mr-2" />{template.visibility?.status || 'draft'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-template" title="Save (Ctrl/Cmd+S)">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? 'Save' : 'Create'}{dirty ? ' •' : ''}
          </Button>
        </div>
      </div>

      <Dialog open={govOpen} onOpenChange={setGovOpen}>
        <DialogContent className="max-w-md" data-testid="governance-dialog">
          <DialogHeader><DialogTitle>Publishing &amp; visibility</DialogTitle></DialogHeader>
          <GovernancePanel
            visibility={template.visibility}
            onChange={(vis) => mutateLive((t) => ({ ...t, visibility: vis }))}
          />
          <DialogFooter>
            <Button onClick={() => setGovOpen(false)} data-testid="governance-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rptOpen} onOpenChange={setRptOpen}>
        <DialogContent className="max-w-md" data-testid="repeat-dialog">
          <DialogHeader><DialogTitle>Repeating groups</DialogTitle></DialogHeader>

          {(template.repeat_groups?.length || 0) > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Existing groups</Label>
              {template.repeat_groups.map((g) => (
                <div key={g.id} className="flex items-center justify-between text-sm border rounded px-2 py-1" data-testid={`rpt-item-${g.id}`}>
                  <span className="capitalize">{g.bind} · {g.layout} · {g.min_count}–{g.max_count} · {g.cell.length} layer(s)</span>
                  <Button variant="ghost" size="icon" onClick={() => removeRepeat(g.id)} title="Remove"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3 pt-2 border-t">
            <Label className="text-xs">Create from {selectedIds.length} selected layer(s)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Repeat over</Label>
                <Select value={rptCfg.bind} onValueChange={(b) => setRptCfg((c) => ({ ...c, bind: b }))}>
                  <SelectTrigger data-testid="rpt-bind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="agents">Agents</SelectItem><SelectItem value="properties">Properties</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Layout</Label>
                <Select value={rptCfg.layout} onValueChange={(l) => setRptCfg((c) => ({ ...c, layout: l }))}>
                  <SelectTrigger data-testid="rpt-layout"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="columns">Columns</SelectItem><SelectItem value="rows">Rows</SelectItem><SelectItem value="stack">Stack</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Min count</Label>
                <Input type="number" min={1} data-testid="rpt-min" value={rptCfg.minCount}
                  onChange={(e) => setRptCfg((c) => ({ ...c, minCount: Math.max(1, Number(e.target.value) || 1) }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max count</Label>
                <Input type="number" min={1} data-testid="rpt-max" value={rptCfg.maxCount}
                  onChange={(e) => setRptCfg((c) => ({ ...c, maxCount: Math.max(1, Number(e.target.value) || 1) }))} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">The selected layers become the repeating cell; on export this compiles to one base PDF per count (1…max). On-canvas reflow preview is a follow-up.</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRptOpen(false)}>Close</Button>
            <Button onClick={applyRepeat} disabled={selectedIds.length === 0} data-testid="rpt-apply">Make repeating</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-12 gap-4">
        {/* Layers panel */}
        <div className="col-span-3 space-y-3">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            <LayersIcon className="h-4 w-4" /> Layers
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={() => addLayer('text')} data-testid="add-text"><Type className="h-3.5 w-3.5 mr-1" />Text</Button>
            <Button size="sm" variant="outline" onClick={() => addLayer('shape')}><Square className="h-3.5 w-3.5 mr-1" />Shape</Button>
            <Button size="sm" variant="outline" onClick={() => addLayer('image')}><ImageIcon className="h-3.5 w-3.5 mr-1" />Image</Button>
            <Button size="sm" variant="outline" onClick={() => addLayer('background')}>Background</Button>
          </div>
          <div className="border rounded-md divide-y max-h-[420px] overflow-y-auto">
            {template.layers.length === 0 && <p className="p-3 text-xs text-muted-foreground">No layers yet. Add one above.</p>}
            {[...template.layers].reverse().map((l) => {
              const hidden = l.visible === false;
              return (
                <div
                  key={l.id}
                  onClick={(e) => ((e.shiftKey || e.metaKey || e.ctrlKey) ? toggleSelect(l.id) : selectOnly(l.id))}
                  data-testid={`layer-row-${l.id}`}
                  className={`group flex items-center justify-between px-2.5 py-1.5 cursor-pointer text-sm ${selectedIds.includes(l.id) ? 'bg-primary/10' : 'hover:bg-muted/50'} ${hidden ? 'opacity-50' : ''}`}
                >
                  <span className="truncate flex items-center gap-1.5">
                    {l.type === 'text' ? <Type className="h-3.5 w-3.5" /> : l.type === 'image' ? <ImageIcon className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                    <span className="truncate">{l.type === 'text' ? (l.text || 'Text').slice(0, 16) : l.type}</span>
                    {l.animation && <Sparkles className="h-3 w-3 text-primary shrink-0" />}
                    {l.locked && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                  </span>
                  <span className={`flex items-center gap-0.5 transition-opacity focus-within:opacity-100 ${selectedIds.includes(l.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    <button title={l.locked ? 'Unlock' : 'Lock'} onClick={(e) => { e.stopPropagation(); toggleLock(l.id); }} className="p-0.5 hover:text-primary">{l.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}</button>
                    <button title="Show/hide" onClick={(e) => { e.stopPropagation(); toggleVisibility(l.id); }} className="p-0.5 hover:text-primary">{hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}</button>
                    <button title="Duplicate" onClick={(e) => { e.stopPropagation(); duplicateLayer(l.id); }} className="p-0.5 hover:text-primary"><Copy className="h-3 w-3" /></button>
                    <button title="Bring forward" onClick={(e) => { e.stopPropagation(); moveLayer(l.id, 'up'); }} className="p-0.5 hover:text-primary"><ArrowUp className="h-3 w-3" /></button>
                    <button title="Send backward" onClick={(e) => { e.stopPropagation(); moveLayer(l.id, 'down'); }} className="p-0.5 hover:text-primary"><ArrowDown className="h-3 w-3" /></button>
                    <button title="Delete" onClick={(e) => { e.stopPropagation(); deleteLayer(l.id); }} className="p-0.5 hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            <span className="font-medium">Shortcuts:</span> drag to move · double-click text to edit · ⌫ delete · arrows nudge (⇧ ×10) · ⌘/Ctrl+D duplicate · ⌘/Ctrl+Z undo · ⌘/Ctrl+S save
          </p>
        </div>

        {/* Canvas */}
        <div className="col-span-5">
          <div ref={canvasWrapRef} className="flex bg-muted/30 border rounded-md p-4 min-h-[520px] overflow-auto">
            <div
              onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedIds([]); }}
              data-testid="artboard"
              style={{
                position: 'relative',
                flexShrink: 0,
                margin: 'auto',
                width: template.artboard.width * scale,
                height: template.artboard.height * scale,
                background: template.artboard.background_color,
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                overflow: 'hidden',
              }}
            >
              {template.layers.map((l) => {
                if (l.visible === false) return null;
                const isBg = l.type === 'background';
                const box = {
                  position: 'absolute',
                  left: isBg ? 0 : l.x * scale,
                  top: isBg ? 0 : l.y * scale,
                  width: isBg ? '100%' : l.width * scale,
                  height: isBg ? '100%' : l.height * scale,
                  opacity: l.opacity ?? 1,
                  outline: selectedIds.includes(l.id) ? '2px solid #154734' : 'none',
                  outlineOffset: 0,
                  cursor: 'move',
                };
                let inner = null;
                if (l.type === 'text') {
                  const soff = (l.shadow_offset ?? 4) * scale;
                  const textStyle = {
                    fontSize: (l.font_size || 40) * scale, color: l.color, textAlign: l.align,
                    fontWeight: l.font_weight === 'bold' ? 700 : 400, lineHeight: l.line_height || 1.2,
                    textTransform: l.uppercase ? 'uppercase' : 'none', width: '100%', height: '100%',
                    fontFamily: cssFontFor(l.font_family),
                    textShadow: l.shadow_color ? `${soff}px ${soff}px 0 ${l.shadow_color}` : 'none',
                    WebkitTextStroke: (l.stroke_width && l.stroke_color) ? `${l.stroke_width * scale}px ${l.stroke_color}` : undefined,
                  };
                  inner = editingId === l.id ? (
                    <textarea
                      autoFocus
                      data-testid="inline-text"
                      value={l.text}
                      onChange={(e) => updateLayer(l.id, { text: e.target.value }, { live: true })}
                      onBlur={() => setEditingId(null)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setEditingId(null); } e.stopPropagation(); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ ...textStyle, border: 'none', outline: '2px solid #154734', resize: 'none', background: 'rgba(255,255,255,0.65)', padding: 0, overflow: 'hidden', fontFamily: 'inherit' }}
                    />
                  ) : (
                    <div style={{ ...textStyle, overflow: 'hidden' }}>{l.text}</div>
                  );
                } else if (l.type === 'shape') {
                  inner = <div style={{ width: '100%', height: '100%', background: l.gradient ? cssGradient(l.gradient) : (l.fill_color || 'transparent'), borderRadius: l.shape === 'ellipse' ? '50%' : (l.border_radius || 0) * scale, border: l.stroke_width ? `${l.stroke_width * scale}px solid ${l.stroke_color}` : 'none' }} />;
                } else if (l.type === 'image' || l.type === 'background') {
                  const src = l.type === 'image' ? l.source : l.image;
                  const isToken = src && src.includes('{{');
                  if (src && !isToken) inner = <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: l.fit || 'cover' }} />;
                  else if (l.type === 'background') inner = <div style={{ width: '100%', height: '100%', background: l.gradient ? cssGradient(l.gradient) : (l.color || '#fff') }} />;
                  else inner = <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: '#e8eeea', color: '#6f8579', fontSize: 11, textAlign: 'center', padding: 4 }}>{isToken ? src : 'image'}</div>;
                }
                return (
                  <div key={l.id} style={box}
                    onMouseDown={(e) => { if (!isBg && editingId !== l.id) startDrag(e, l); }}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => { if (l.type === 'text') { e.stopPropagation(); snapshot(); selectOnly(l.id); setEditingId(l.id); } }}>
                    {inner}
                    {selected && selected.id === l.id && !isBg && !l.locked && (
                      <div onMouseDown={(e) => startResize(e, l)} style={{ position: 'absolute', right: -5, bottom: -5, width: 10, height: 10, background: '#154734', cursor: 'nwse-resize', borderRadius: 2 }} />
                    )}
                  </div>
                );
              })}
              {/* Snap guides */}
              {guides.v != null && <div style={{ position: 'absolute', left: guides.v * scale, top: 0, width: 1, height: '100%', background: '#ba0c2f', pointerEvents: 'none', zIndex: 50 }} />}
              {guides.h != null && <div style={{ position: 'absolute', top: guides.h * scale, left: 0, height: 1, width: '100%', background: '#ba0c2f', pointerEvents: 'none', zIndex: 50 }} />}
            </div>
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <button type="button" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.25, +(z / 1.25).toFixed(3)))} data-testid="zoom-out" className="h-7 w-7 grid place-items-center rounded border hover:bg-muted text-muted-foreground"><Minus className="h-3.5 w-3.5" /></button>
            <span className="text-xs tabular-nums w-12 text-center" data-testid="zoom-level">{Math.round(scale * 100)}%</span>
            <button type="button" title="Zoom in" onClick={() => setZoom((z) => Math.min(4, +(z * 1.25).toFixed(3)))} data-testid="zoom-in" className="h-7 w-7 grid place-items-center rounded border hover:bg-muted text-muted-foreground"><Plus className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => setZoom(1)} data-testid="zoom-fit" className="h-7 px-2 text-xs rounded border hover:bg-muted">Fit</button>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 text-center">Approximate canvas — snap guides help alignment; use Render for the true output.</p>
        </div>

        {/* Properties + preview */}
        <div className="col-span-4 space-y-4">
          {/* Motion / MP4 */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <Film className="h-4 w-4 text-primary" /> Animate (MP4)
                </span>
                <input
                  type="checkbox"
                  checked={animated}
                  onChange={(e) => toggleAnimation(e.target.checked)}
                  data-testid="animate-toggle"
                />
              </label>
              {animated ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px]">Duration (s)</Label>
                      <Input type="number" min="1" max="30" step="0.5" className="h-8" data-testid="video-duration"
                        value={template.video.duration}
                        onChange={(e) => patchVideo({ duration: Number(e.target.value) })} />
                    </div>
                    <div>
                      <Label className="text-[11px]">FPS</Label>
                      <Select value={String(template.video.fps)} onValueChange={(v) => patchVideo({ fps: Number(v) })}>
                        <SelectTrigger className="h-8" data-testid="video-fps"><SelectValue /></SelectTrigger>
                        <SelectContent>{[12, 24, 30].map((f) => <SelectItem key={f} value={String(f)}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Give layers a motion preset below, then press <span className="font-medium">Preview clip</span>. Exports as MP4 for Reels / Stories.
                  </p>
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground">Turn on to add motion and export this template as a video clip.</p>
              )}
            </CardContent>
          </Card>

          {selected ? (
            <Card>
              <CardContent className="p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold capitalize">{selected.type} properties</div>
                  <div className="flex items-center gap-2">
                    <button title={selected.locked ? 'Unlock' : 'Lock'} onClick={() => toggleLock(selected.id)} data-testid="lock-layer" className="text-muted-foreground hover:text-primary">{selected.locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}</button>
                    <button title="Duplicate (⌘/Ctrl+D)" onClick={() => duplicateLayer(selected.id)} data-testid="duplicate-layer" className="text-muted-foreground hover:text-primary"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                </div>

                <div>
                  <Label className="text-[11px]">Opacity — {Math.round((selected.opacity ?? 1) * 100)}%</Label>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    value={selected.opacity ?? 1}
                    onChange={(e) => updateLayer(selected.id, { opacity: Number(e.target.value) })}
                    data-testid="layer-opacity"
                    className="w-full accent-[#154734]"
                  />
                </div>

                {/* Alignment toolbar */}
                {selected.type !== 'background' && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase text-muted-foreground mr-1">Align</span>
                    {alignBtn('h', 'left', AlignLeft, 'Left')}
                    {alignBtn('h', 'center', AlignCenter, 'Centre H', 'align-h-center')}
                    {alignBtn('h', 'right', AlignRight, 'Right')}
                    <span className="w-px h-4 bg-border mx-0.5" />
                    {alignBtn('v', 'top', ArrowUpToLine, 'Top')}
                    {alignBtn('v', 'center', Minus, 'Centre V')}
                    {alignBtn('v', 'bottom', ArrowDownToLine, 'Bottom')}
                  </div>
                )}

                <div className="grid grid-cols-4 gap-1.5">
                  {['x', 'y', 'width', 'height'].map((k) => (
                    <div key={k}>
                      <Label className="text-[11px] uppercase">{k[0]}</Label>
                      <Input type="number" className="h-8 text-xs px-1.5" data-testid={`pos-${k}`} value={Math.round(selected[k] ?? 0)} onChange={(e) => updateLayer(selected.id, { [k]: Number(e.target.value) })} />
                    </div>
                  ))}
                </div>

                {selected.type === 'text' && (
                  <>
                    <div>
                      <Label className="text-xs">Text</Label>
                      <textarea className="w-full text-sm border rounded-md p-2 min-h-[60px]" value={selected.text} onChange={(e) => updateLayer(selected.id, { text: e.target.value })} data-testid="layer-text" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-[11px]">Font</Label>
                          <button type="button" onClick={() => fontUploadRef.current?.click()} data-testid="upload-font" className="text-[10px] text-primary hover:underline">Upload…</button>
                        </div>
                        <Select value={selected.font_family || 'sans'} onValueChange={(v) => updateLayer(selected.id, { font_family: v })}>
                          <SelectTrigger className="h-8" data-testid="layer-font"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FONT_FAMILIES.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                            {customFonts.map((f) => <SelectItem key={f.family} value={f.family}>{f.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <input ref={fontUploadRef} type="file" accept=".ttf,.otf" className="hidden" data-testid="font-upload-input" onChange={handleFontUpload} />
                      </div>
                      <div><Label className="text-[11px]">Size</Label><Input type="number" className="h-8" value={selected.font_size} onChange={(e) => updateLayer(selected.id, { font_size: Number(e.target.value) })} /></div>
                    </div>
                    <div>
                      <Label className="text-[11px]">Align</Label>
                      <Select value={selected.align} onValueChange={(v) => updateLayer(selected.id, { align: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{['left', 'center', 'right'].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <ColorField label="Colour" value={selected.color} recent={recentColors} onChange={(v) => updateLayer(selected.id, { color: v })} />
                    <div className="flex gap-3 text-xs">
                      <label className="flex items-center gap-1"><input type="checkbox" checked={selected.font_weight === 'bold'} onChange={(e) => updateLayer(selected.id, { font_weight: e.target.checked ? 'bold' : 'regular' })} /> Bold</label>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={!!selected.uppercase} onChange={(e) => updateLayer(selected.id, { uppercase: e.target.checked })} /> Uppercase</label>
                    </div>

                    {/* Effects: outline (needs width + colour) and drop shadow (needs colour) */}
                    <div className="pt-2 border-t space-y-2">
                      <Label className="text-xs">Effects</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[11px]">Outline width</Label>
                          <Input type="number" min="0" className="h-8" data-testid="text-stroke-width"
                            value={selected.stroke_width || 0}
                            onChange={(e) => updateLayer(selected.id, { stroke_width: Number(e.target.value) })} />
                        </div>
                        <div>
                          <Label className="text-[11px]">Shadow offset</Label>
                          <Input type="number" min="0" className="h-8"
                            value={selected.shadow_offset ?? 4}
                            onChange={(e) => updateLayer(selected.id, { shadow_offset: Number(e.target.value) })} />
                        </div>
                      </div>
                      <ColorField label="Outline colour" prefix="outline" value={selected.stroke_color} recent={recentColors} onChange={(v) => updateLayer(selected.id, { stroke_color: v })} />
                      <ColorField label="Shadow colour" prefix="shadow" value={selected.shadow_color} recent={recentColors} onChange={(v) => updateLayer(selected.id, { shadow_color: v })} />
                    </div>
                  </>
                )}

                {selected.type === 'shape' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">Shape</Label>
                        <Select value={selected.shape} onValueChange={(v) => updateLayer(selected.id, { shape: v })}>
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="rect">Rectangle</SelectItem><SelectItem value="ellipse">Ellipse</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-[11px]">Corner radius</Label><Input type="number" className="h-8" value={selected.border_radius || 0} onChange={(e) => updateLayer(selected.id, { border_radius: Number(e.target.value) })} /></div>
                    </div>
                    <ColorField label="Fill" value={selected.fill_color} recent={recentColors} onChange={(v) => updateLayer(selected.id, { fill_color: v })} />
                    <div className="pt-2 border-t">
                      <GradientField value={selected.gradient} recent={recentColors} onChange={(g) => updateLayer(selected.id, { gradient: g })} />
                    </div>
                  </div>
                )}

                {selected.type === 'image' && (
                  <div>
                    <Label className="text-xs">Source (URL or dynamic field)</Label>
                    <Input className="h-8" value={selected.source} onChange={(e) => updateLayer(selected.id, { source: e.target.value })} placeholder="https://… or {{agent.image.portrait}}" data-testid="layer-source" />
                    <Button
                      size="sm" variant="outline" className="w-full mt-1.5"
                      onClick={() => { pickerLayerRef.current = selected.id; setPickerOpen(true); }}
                      data-testid="open-image-picker"
                    >
                      <ImageIcon className="h-3.5 w-3.5 mr-1" /> Choose from library / upload
                    </Button>
                    <div className="mt-1">
                      <Label className="text-[11px]">Fit</Label>
                      <Select value={selected.fit} onValueChange={(v) => updateLayer(selected.id, { fit: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cover">Cover</SelectItem><SelectItem value="contain">Contain</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {selected.type === 'background' && (
                  <div className="space-y-2">
                    <ColorField label="Colour" value={selected.color} recent={recentColors} onChange={(v) => updateLayer(selected.id, { color: v })} />
                    <div className="pt-2 border-t">
                      <GradientField value={selected.gradient} recent={recentColors} onChange={(g) => updateLayer(selected.id, { gradient: g })} />
                    </div>
                  </div>
                )}

                {/* Dynamic field palette */}
                {(selected.type === 'text' || selected.type === 'image') && (
                  <div className="pt-2 border-t">
                    <Label className="text-xs">Insert dynamic field</Label>
                    <div className="space-y-1.5 mt-1">
                      {DYNAMIC_FIELDS.filter((g) => selected.type === 'image' ? g.image : !g.image).map((g) => (
                        <div key={g.group}>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{g.group}</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {g.items.map((it) => (
                              <button key={it.token} type="button" onClick={() => insertField(it.token, g.image)} data-testid={`field-${it.token}`}
                                title={it.alchemy ? `Publishes to Alchemy as ${it.alchemy.block}` : 'LinkHub-only field (no Alchemy mapping)'}
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border hover:border-primary hover:bg-primary/5">
                                {it.label}
                                {it.alchemy && (
                                  <span data-testid={`alchemy-badge-${it.token}`} aria-label="maps to Alchemy"
                                    className="w-1.5 h-1.5 rounded-full bg-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-layer animation (only for animated templates) */}
                {animated && selected.type !== 'background' && (() => {
                  const anim = selected.animation || { preset: 'none', delay: 0, duration: 0.6 };
                  const preset = ANIM_PRESETS.find((p) => p.key === anim.preset) || ANIM_PRESETS[0];
                  return (
                    <div className="pt-2 border-t space-y-2">
                      <Label className="text-xs flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-primary" /> Animation</Label>
                      <Select value={anim.preset} onValueChange={(v) => setLayerAnim(selected.id, { preset: v })}>
                        <SelectTrigger className="h-8" data-testid="layer-anim-preset"><SelectValue /></SelectTrigger>
                        <SelectContent>{ANIM_PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {preset.timed && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[11px]">Delay (s)</Label>
                            <Input type="number" min="0" step="0.1" className="h-8" data-testid="layer-anim-delay"
                              value={anim.delay ?? 0} onChange={(e) => setLayerAnim(selected.id, { delay: Number(e.target.value) })} />
                          </div>
                          <div>
                            <Label className="text-[11px]">Duration (s)</Label>
                            <Input type="number" min="0.1" step="0.1" className="h-8" data-testid="layer-anim-duration"
                              value={anim.duration ?? 0.6} onChange={(e) => setLayerAnim(selected.id, { duration: Number(e.target.value) })} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ) : selectedIds.length > 1 ? (
            <Card>
              <CardContent className="p-3 space-y-3" data-testid="multi-props">
                <div className="text-sm font-semibold">{selectedIds.length} layers selected</div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase text-muted-foreground mr-1">Align</span>
                  {alignBtn('h', 'left', AlignLeft, 'Left')}
                  {alignBtn('h', 'center', AlignCenter, 'Centre H', 'align-h-center')}
                  {alignBtn('h', 'right', AlignRight, 'Right')}
                  <span className="w-px h-4 bg-border mx-0.5" />
                  {alignBtn('v', 'top', ArrowUpToLine, 'Top')}
                  {alignBtn('v', 'center', Minus, 'Centre V')}
                  {alignBtn('v', 'bottom', ArrowDownToLine, 'Bottom')}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => duplicateLayers(selectedIds)} data-testid="multi-duplicate">
                    <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteLayers(selectedIds)} data-testid="multi-delete">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Shift-click layers to add or remove them. Drag any selected layer to move them together.</p>
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="p-4 text-sm text-muted-foreground">Select a layer to edit its properties, or add one. Shift-click to select several.</CardContent></Card>
          )}

          {/* Preview */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">True preview</span>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={runPreview} disabled={previewing} data-testid="run-preview">
                    {previewing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1" />} Still
                  </Button>
                  {animated && (
                    <Button size="sm" variant="outline" onClick={runVideoPreview} disabled={videoPreviewing} data-testid="run-video-preview">
                      {videoPreviewing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />} Clip
                    </Button>
                  )}
                </div>
              </div>
              {selectedAgent ? (
                <div className="flex items-center justify-between text-xs border rounded px-2 py-1">
                  <span className="flex items-center gap-1"><User className="h-3 w-3" />{selectedAgent.name}</span>
                  <button onClick={() => setSelectedAgent(null)} className="text-muted-foreground hover:text-foreground">clear</button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input className="h-8 pl-7 text-xs" placeholder="Bind an agent (optional)…" value={agentQuery} onChange={(e) => setAgentQuery(e.target.value)} data-testid="preview-agent-search" />
                  {agents.length > 0 && (
                    <div className="border rounded mt-1 divide-y max-h-32 overflow-y-auto">
                      {agents.map((a) => <button key={a.id} onClick={() => { setSelectedAgent(a); setAgentQuery(''); setAgents([]); }} className="w-full text-left px-2 py-1 text-xs hover:bg-muted/50">{a.name}</button>)}
                    </div>
                  )}
                </div>
              )}
              <div className="border rounded bg-muted/30 min-h-[160px] flex items-center justify-center overflow-hidden">
                {previewUrl ? <img src={previewUrl} alt="preview" data-testid="editor-preview" className="max-h-[300px] w-auto" /> : <span className="text-xs text-muted-foreground p-4">Click Still for the true server preview.</span>}
              </div>
              {animated && videoUrl && (
                <div className="border rounded bg-black/90 flex items-center justify-center overflow-hidden">
                  <video src={videoUrl} data-testid="editor-video-preview" className="max-h-[300px] w-auto" autoPlay loop muted playsInline controls />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <CollateralImagePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(url) => { if (pickerLayerRef.current) updateLayer(pickerLayerRef.current, { source: url }); }}
      />
    </div>
  );
}
